/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/** Voice mode is shown only when this is "true" (baked in at build time). */
export const VOICE_AGENT_ENABLED = process.env.REACT_APP_VOICE_AGENT_ENABLED === 'true';

/** Server path of the voice WebSocket (server VOICE_AGENT_PATH; not under /api). */
export const VOICE_AGENT_WS_PATH = '/ws/voice';

/**
 * The voice WebSocket URL: REACT_APP_VOICE_AGENT_WS_URL, else derived from
 * REACT_APP_API_URL (http → ws, https → wss).
 */
export const voiceAgentUrl = () =>
  process.env.REACT_APP_VOICE_AGENT_WS_URL
  || `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '').replace(/^http/i, 'ws')}${VOICE_AGENT_WS_PATH}`;

export const VOICE_INPUT_SAMPLE_RATE = 16000;

/**
 * Client-side cap on one voice session (REACT_APP_VOICE_SESSION_MAX_SECONDS):
 * a countdown is shown once connected and the session ends at 0. 0 = no limit.
 */
const parsedMaxSeconds = parseInt(process.env.REACT_APP_VOICE_SESSION_MAX_SECONDS, 10);
export const VOICE_SESSION_MAX_SECONDS = Number.isFinite(parsedMaxSeconds) && parsedMaxSeconds > 0 ? parsedMaxSeconds : 0;

/** The countdown turns to warning colour in the last seconds of a session. */
export const VOICE_SESSION_WARN_SECONDS = 30;
export const VOICE_SESSION_CRITICAL_SECONDS = 10;

/** Seconds as m:ss (e.g. 245 → "4:05"). */
export const formatVoiceTimer = (seconds) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Label per server session state (state.changed). */
export const VOICE_STATE_LABELS = {
  idle: 'Tap to start a conversation with the agent',
  connecting: 'Connecting…',
  listening: 'Listening — go ahead',
  user_speaking: 'You’re speaking…',
  thinking: 'Thinking…',
  agent_speaking: 'Agent is speaking — talk to interrupt',
  muted: 'Muted — the agent can’t hear you',
};

/** User-facing text per WebSocket close code (server VOICE_CLOSE_CODES). */
export const VOICE_CLOSE_MESSAGES = {
  1001: 'The voice service restarted. Start the conversation again to continue.',
  1011: 'The voice conversation failed. Please start it again.',
  1013: 'The voice service is busy or unavailable. Please try again shortly.',
  4400: 'The voice session could not start.',
  4401: 'Your session has expired. Start the conversation again to reconnect.',
  4403: 'You do not have access to talk to this agent.',
  4408: 'The voice session ended after a period of inactivity.',
  4413: 'The audio stream was rejected.',
  4429: 'Too much audio was sent. Please start the conversation again.',
};

export const VOICE_ERR_MIC_DENIED = 'Microphone access was blocked. Allow it in the browser to use voice mode.';
export const VOICE_ERR_NO_MIC = 'No microphone was found.';
export const VOICE_ERR_GENERIC = 'Voice mode is unavailable right now.';
export const VOICE_ERR_TIME_LIMIT = 'The voice conversation reached its time limit. Start a new conversation to continue.';
